'use client';

import { useParams, useRouter } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useTransition,
} from 'react';
import { toast } from 'sonner';
import type { OrderComplete } from '@/features/orders/domain/model/order';
import { getCashNumber } from '@/features/orders/domain/service/order-util';
import { closeOrder } from '@/features/orders/presentation/actions/orders/close-order';
import { getOrder } from '@/features/orders/presentation/actions/orders/get-order';
import { usePaymentMethods } from '@/features/orders/presentation/hooks/orders/use-payment-methods';
import type { PaymentMethod } from '@/features/payments/domain/model/payment-method';
import type { TakeOrderFlowEvent } from '@/features/totems/domain/model/take-order-flow-events';
import { resolveCheckoutMethodRoute } from '@/features/totems/domain/service/checkout-method-route';
import {
  cancelPendingTotemOrder,
  createTotemOrder,
} from '@/features/totems/presentation/actions/web-totem/create-totem-order';
import {
  checkoutFlowReducer,
  type CheckoutFlowState,
  initialCheckoutFlowState,
} from '@/features/totems/presentation/components/web-totem/checkout/checkout-flow-reducer';
import { useOrderPaymentWatcher } from '@/features/totems/presentation/hooks/web-totem/use-order-payment-watcher';
import {
  useTotemActions,
  useTotemCartState,
  useTotemCatalog,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { supabase } from '@/shared/infrastructure/supabase/client';

/** Estado que leen los steps y el shell del checkout. */
export type CheckoutFlowStateValue = CheckoutFlowState & {
  isPending: boolean;
  /** `true` cuando el modal ya no está en el paso de elegir medio. */
  isStarting: boolean;
  isTakeAway: boolean;
  amountTendered: string;
  availableMethods: PaymentMethod[];
  isWebTotem: boolean;
  /** Panel de caja (`/[locationId]/takeorder`) vs carta/tótem público. */
  isDashboard: boolean;
};

/** Acciones estables: no cambian de identidad y no re-renderizan a quien solo las usa. */
export type CheckoutFlowActionsValue = {
  selectMethod: (method: PaymentMethod) => void;
  goBack: () => void;
  closeDialog: () => void;
  /** Reintenta el cierre con el mismo `orderId` (idempotente). */
  retryClose: () => void;
  setIsTakeAway: (value: boolean) => void;
  setAmountTendered: (value: string) => void;
};

const CheckoutFlowStateCtx = createContext<CheckoutFlowStateValue | null>(null);
const CheckoutFlowActionsCtx = createContext<CheckoutFlowActionsValue | null>(null);

export function useCheckoutFlowState(): CheckoutFlowStateValue {
  const value = useContext(CheckoutFlowStateCtx);
  if (!value) {
    throw new Error('useCheckoutFlowState debe usarse dentro de CheckoutFlowProvider');
  }
  return value;
}

export function useCheckoutFlowActions(): CheckoutFlowActionsValue {
  const value = useContext(CheckoutFlowActionsCtx);
  if (!value) {
    throw new Error('useCheckoutFlowActions debe usarse dentro de CheckoutFlowProvider');
  }
  return value;
}

/** Estado + acciones (re-renderiza con cualquier cambio); preferir el slice que se necesita. */
export function useCheckoutFlow(): CheckoutFlowStateValue & CheckoutFlowActionsValue {
  const state = useCheckoutFlowState();
  const actions = useCheckoutFlowActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}

type Props = {
  /** Despachador del mediador (`TakeOrderFlowProvider`). */
  onEvent: (event: TakeOrderFlowEvent) => void;
  children: ReactNode;
};

/**
 * Flujo de pago: máquina de steps, orden, QR, watcher y navegación.
 *
 * No imprime ni vacía el carrito — esos efectos viajan al mediador como `checkout_completed`.
 */
export function CheckoutFlowProvider({ onEvent, children }: Props) {
  const router = useRouter();
  const params = useParams();
  const { locationId } = useParams<{ locationId: string }>();
  const { totem } = useTotemCatalog();
  const { cart } = useTotemCartState();
  const { setCheckoutOpen } = useTotemActions();

  const [state, dispatch] = useReducer(checkoutFlowReducer, initialCheckoutFlowState);
  const [isPending, startTransition] = useTransition();
  const [isTakeAway, setIsTakeAway] = useState(false);
  const [amountTendered, setAmountTendered] = useState('');

  const orderIdRef = useRef<string | null>(null);
  useEffect(() => {
    orderIdRef.current = state.orderId;
  }, [state.orderId]);

  const locationSlug = (params?.locationId as string) || (params?.slug as string) || totem?.locations?.slug;
  const totemSlug = totem?.slug;
  const isWebTotem = Boolean(totem?.web);
  // El panel de caja vive en `/[locationId]/takeorder`; la carta y el tótem público usan `slug`.
  const isDashboard = Boolean(locationId);

  const { data: paymentMethods } = usePaymentMethods({ locationIdFallback: locationId ?? locationSlug });

  const availableMethods = useMemo<PaymentMethod[]>(() => {
    const methods = paymentMethods?.paymentMethods ?? [];

    // Panel de caja / dashboard takeorder: agnóstico al tótem, muestra todos los métodos activos.
    if (locationId) return methods;

    // Carta + pedido móvil: exclusivamente Mercado Pago (QR u online checkout).
    if (isWebTotem) {
      return methods.filter(
        method =>
          method.type === 'mercado_pago' ||
          method.type === 'qr' ||
          method.name.toLowerCase().includes('mercado') ||
          method.name.toLowerCase().includes('mp')
      );
    }

    // Tótem físico de autoservicio: muestra los métodos configurados para ese tótem.
    return methods.filter(method => totem?.payment_methods?.includes(method.id));
  }, [locationId, isWebTotem, paymentMethods?.paymentMethods, totem?.payment_methods]);

  const clearLocalState = useCallback(() => {
    dispatch({ type: 'reset' });
    setIsTakeAway(false);
    setAmountTendered('');
  }, []);

  /** Cierre por éxito: NO cancela la orden (ya está cobrada o en curso de cobro). */
  const finishFlow = useCallback(() => {
    setCheckoutOpen(false);
    clearLocalState();
  }, [setCheckoutOpen, clearLocalState]);

  /** Abandono explícito: cancela la orden pendiente creada en este intento. */
  const abandonFlow = useCallback(async () => {
    const pendingOrderId = orderIdRef.current;
    clearLocalState();
    if (pendingOrderId) {
      await cancelPendingTotemOrder(pendingOrderId);
    }
  }, [clearLocalState]);

  const closeDialog = useCallback(async () => {
    setCheckoutOpen(false);
    await abandonFlow();
  }, [setCheckoutOpen, abandonFlow]);

  const redirectToSuccess = useCallback(
    (orderIdToRedirect: string) => {
      if (isDashboard && locationSlug) {
        router.push(`/${locationSlug}/takeorder/success/${orderIdToRedirect}`);
      } else {
        router.push(`/orders/${orderIdToRedirect}`);
      }
    },
    [isDashboard, locationSlug, router]
  );

  const createOrder = useCallback(
    async (methodId: string) => {
      if (!totem) return null;

      const result = await createTotemOrder({
        totemId: totem.id,
        items: cart.toOrderPayload(),
        isTakeAway,
        paymentMethodId: methodId,
        skipCashNum: isDashboard,

        discount: cart.discount ? { discountId: cart.discount.id } : undefined,
      });

      if (!result) {
        toast.error('No se pudo crear el pedido');
        return null;
      }

      return result.orderId;
    },
    [totem, cart, isTakeAway, isDashboard]
  );

  /**
   * Cierra la orden cobrada y navega al éxito. Si el cierre falla, deja el checkout en
   * `payment_error` con reintento por el mismo `orderId` (no duplica la orden).
   */
  const closeAndComplete = useCallback(
    async (pendingOrderId: string) => {
      dispatch({ type: 'close_order' });

      try {
        await closeOrder({ locationId: locationSlug ?? undefined, orderId: pendingOrderId });
      } catch (error) {
        console.error('[CheckoutFlow] No se pudo cerrar la orden', error);
        dispatch({
          type: 'fail',
          message: 'No pudimos confirmar el cobro. Reintentá para asignar el número del pedido.',
          canRetryClose: true,
        });
        return;
      }

      // El número lo asigna la edge function `payment-operation`; se relee para imprimir el definitivo.
      let closedOrder = await getOrder({ orderId: pendingOrderId }).catch(() => null);

      // Tolerancia mínima a consistencia eventual: una segunda lectura antes de dar el cierre por fallido.
      if (closedOrder && closedOrder.number == null) {
        await new Promise(resolve => window.setTimeout(resolve, 400));
        closedOrder = await getOrder({ orderId: pendingOrderId }).catch(() => null);
      }

      if (!closedOrder || closedOrder.number == null) {
        dispatch({
          type: 'fail',
          message: 'El cobro no terminó de confirmarse. Reintentá para asignar el número del pedido.',
          canRetryClose: true,
        });
        return;
      }

      // Comanda de cocina y ticket de caja con el número ya asignado; la pantalla de éxito no reimprime.
      onEvent({ type: 'checkout_completed', order: closedOrder, paidVia: 'cash' });
      finishFlow();
      redirectToSuccess(pendingOrderId);
    },
    [finishFlow, locationSlug, onEvent, redirectToSuccess]
  );

  const retryClose = useCallback(() => {
    const pendingOrderId = orderIdRef.current;
    if (!pendingOrderId) {
      dispatch({ type: 'back' });
      return;
    }

    startTransition(async () => {
      await closeAndComplete(pendingOrderId);
    });
  }, [closeAndComplete]);

  const handleCashPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        dispatch({ type: 'start', methodType: method.type });
        const createdOrderId = await createOrder(method.id);
        if (!createdOrderId) {
          dispatch({ type: 'back' });
          return;
        }
        dispatch({ type: 'order_created', orderId: createdOrderId });

        // Dashboard: se cierra la orden antes de navegar (el número definitivo viaja en la comanda).
        if (isDashboard) {
          await closeAndComplete(createdOrderId);
          return;
        }

        // Tótem público: se imprime en el driver local del tótem y se muestra el código de caja.
        let orderData: OrderComplete | null = null;
        try {
          orderData = await getOrder({ orderId: createdOrderId });
        } catch (error) {
          console.error('Error fetching order', error);
        }

        const cashNumber = orderData ? getCashNumber(orderData.cash_num || orderData.sort_id || 0) : null;
        dispatch({ type: 'cashier_confirmation', cashNumber });
        onEvent({ type: 'checkout_completed', order: orderData, paidVia: 'cash' });

        window.setTimeout(() => {
          finishFlow();
          if (totemSlug) router.push(`/totem-by-slug/${totemSlug}`);
        }, 5000);
      });
    },
    [closeAndComplete, createOrder, finishFlow, isDashboard, onEvent, router, totemSlug]
  );

  const handleMpLinkPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        dispatch({ type: 'start', methodType: method.type });
        const createdOrderId = await createOrder(method.id);
        if (!createdOrderId) {
          dispatch({ type: 'back' });
          return;
        }
        dispatch({ type: 'order_created', orderId: createdOrderId });

        const { createMpPreference } =
          await import('@/features/totems/presentation/actions/web-totem/create-mp-preference');

        const result = await createMpPreference(createdOrderId, { returnTo: isDashboard ? 'dashboard' : 'public' });

        if (!result.ok) {
          toast.error(result.error || 'No se pudo generar el link de Mercado Pago', { duration: 8000 });
          dispatch({ type: 'back' });
          return;
        }

        dispatch({ type: 'redirect_to_mp' });
        window.location.href = result.initPoint;
      });
    },
    [createOrder, isDashboard]
  );

  const handleQrPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        dispatch({ type: 'start', methodType: method.type });
        const createdOrderId = await createOrder(method.id);
        if (!createdOrderId) {
          dispatch({ type: 'back' });
          return;
        }
        dispatch({ type: 'order_created', orderId: createdOrderId });

        const { createQrPayment } = await import('@/features/totems/presentation/actions/web-totem/create-qr');
        const result = await createQrPayment(createdOrderId);

        if (!result) {
          toast.error('No pudimos generar el código QR en la terminal');
          dispatch({ type: 'back' });
          return;
        }

        dispatch({ type: 'await_qr', qrMode: result.mode, qrData: result.qrData });
      });
    },
    [createOrder]
  );

  const handleCardPayment = useCallback(
    (method: PaymentMethod) => {
      startTransition(async () => {
        dispatch({ type: 'start', methodType: method.type });
        const createdOrderId = await createOrder(method.id);
        if (!createdOrderId) {
          dispatch({ type: 'back' });
          return;
        }
        dispatch({ type: 'order_created', orderId: createdOrderId });
        dispatch({ type: 'await_terminal' });

        const { error: paymentError } = await supabase.functions.invoke('payment-operation', {
          body: { orderId: createdOrderId },
          method: 'POST',
        });

        if (paymentError) {
          toast.error('No se pudo iniciar el pago en el terminal');
          dispatch({ type: 'back' });
        }
      });
    },
    [createOrder]
  );

  const selectMethod = useCallback(
    (method: PaymentMethod) => {
      const route = resolveCheckoutMethodRoute({
        methodType: method.type,
        isDashboard,
        isWebTotem,
      });

      if (route === 'cash') {
        handleCashPayment(method);
      } else if (route === 'mp_link') {
        handleMpLinkPayment(method);
      } else if (route === 'qr') {
        handleQrPayment(method);
      } else {
        handleCardPayment(method);
      }
    },
    [handleCardPayment, handleCashPayment, handleMpLinkPayment, handleQrPayment, isDashboard, isWebTotem]
  );

  const goBack = useCallback(() => {
    if (state.step === 'select_method') {
      void closeDialog();
      return;
    }

    // No se abandona un cobro en curso: el cierre o la redirección a MP ya están en vuelo.
    if (state.step === 'closing_order' || state.step === 'redirecting_to_mp') return;

    // En la confirmación del tótem público la orden ya se generó y se cobra en caja: solo se vuelve.
    if (state.step === 'cashier_confirmation') {
      dispatch({ type: 'back' });
      return;
    }

    // "Atrás / Cambiar medio" cancela la orden pendiente de este intento: no queda huérfana en /orders.
    const pendingOrderId = orderIdRef.current;
    dispatch({ type: 'reset' });
    if (pendingOrderId) {
      void cancelPendingTotemOrder(pendingOrderId);
    }
  }, [closeDialog, state.step]);

  const handlePaymentApproved = useCallback(
    (paidOrderId: string) => {
      // La pantalla de éxito imprime: acá solo se vacía el carrito.
      onEvent({ type: 'checkout_completed', order: null, paidVia: 'online', channels: [] });
      finishFlow();
      redirectToSuccess(paidOrderId);
    },
    [finishFlow, onEvent, redirectToSuccess]
  );

  const handlePaymentRejected = useCallback(() => {
    dispatch({
      type: 'fail',
      message: 'El medio de pago rechazó la operación. Probá con otro medio o volvé a intentarlo.',
      canRetryClose: false,
    });
  }, []);

  const handlePaymentTimeout = useCallback(() => {
    dispatch({
      type: 'fail',
      message: 'Pasó el tiempo de espera del pago. Volvé a elegir el medio para intentarlo de nuevo.',
      canRetryClose: false,
    });
  }, []);

  useOrderPaymentWatcher({
    orderId: state.orderId,
    enabled: state.step === 'awaiting_customer_payment' || state.step === 'awaiting_terminal',
    onApproved: handlePaymentApproved,
    onRejected: handlePaymentRejected,
    onTimeout: handlePaymentTimeout,
  });

  const stateValue = useMemo<CheckoutFlowStateValue>(
    () => ({
      ...state,
      isPending,
      isStarting: state.step !== 'select_method',
      isTakeAway,
      amountTendered,
      availableMethods,
      isWebTotem,
      isDashboard,
    }),
    [state, isPending, isTakeAway, amountTendered, availableMethods, isWebTotem, isDashboard]
  );

  const actionsValue = useMemo<CheckoutFlowActionsValue>(
    () => ({ selectMethod, goBack, closeDialog, retryClose, setIsTakeAway, setAmountTendered }),
    [selectMethod, goBack, closeDialog, retryClose]
  );

  return (
    <CheckoutFlowActionsCtx.Provider value={actionsValue}>
      <CheckoutFlowStateCtx.Provider value={stateValue}>{children}</CheckoutFlowStateCtx.Provider>
    </CheckoutFlowActionsCtx.Provider>
  );
}
